import React, { useCallback, useEffect, useState } from "react";
import Spacing from "../../Spacing";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import { can_visit, debounce, getPractice, getSpeciality, notify } from "../../../helpers";
import { Plus, Search } from "lucide-react";
import DataTablePagination from "../../DataTablePagination";
import PromptDataTable from "../../DataTable/PromptDataTable";
import { getAllPrompts, deletePrompt } from "../../../services/prompt";
import AddAndEditPromptModal from "../../Modal/AddAndEditPromptModal";
import PromptSingleViewModal from "../../Modal/PromptSingleViewModal";

export default function Prompt({selectedPracticeChange, fetchChangePracticeData, changePracticeSpeciality}) {
  let account_id = getPractice("account_id");
  let location_id = getPractice("location_id");
  let speciality_id = getSpeciality()?.id || 32;
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
    speciality_id = selectedPracticeChange?.specialities[0]?.id || 32;
  }
  if (changePracticeSpeciality != null) {
    speciality_id = changePracticeSpeciality?.id || 32;
  }

  const [enableLoader, setEnableLoader] = useState(false);
  const [promptData, setPromptData] = useState([]);
  const [newPromptModalOpen, setNewPromptModalOpen] = useState(false);
  const [editPrompModalOpen, setEditPrompModalOpen] = useState(false);
  const [prompEditData, setPrompEditData] = useState({});
  const [openPromptViewModal, setOpenPromptViewModal] = useState(false);
  const [openPromptViewId, setOpenPromptViewId] = useState(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const defaultFilter = {
    account_id: account_id,
    location_id: location_id,
    speciality_id: speciality_id,
    page: 1,
    per_page: 10,
    search_term: "",
    show_all: false
  };
  const [filter, setFilter] = useState(defaultFilter);
  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchPromptData(filter);
  }, [filter]);

  const fetchPromptData = async (allfilter) => {
    setEnableLoader(true);
    try {
      const response = await getAllPrompts(allfilter);
      if (response) {
        if(response?.data){
          setPromptData(response?.data?.results);
          setDataCount({
            totalCount: response?.data?.count,
            totalPages: response?.data?.total_pages,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setEnableLoader(false);
    }
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setEnableLoader(true);
    debouncedSetFilter(newFilter);
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setEnableLoader(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setEnableLoader(true);
  };

  const handleDeleteRecord = async (id) => {
    if(can_visit("administrator") && can_visit("administrator_insurance_group")){
      try {
        setEnableLoader(true);
        const result = await deletePrompt(id, account_id, location_id);
        if(result){
          if(result?.data?.status === 'success'){
            notify("success", result?.data?.message, 0.5);
            await fetchPromptData(filter);
          } else {
            notify("error", result?.data?.message, 0.5);
          }
        }
      } catch (error) {
        if (error?.response && error?.response?.data && error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail, 0.5);
        }
      } finally {
        setEnableLoader(false);
      }
    }
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;
    const sameSpec = filter.speciality_id === speciality_id;

    if (sameLocation && sameAccount && sameSpec) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
      speciality_id: speciality_id,
    });
    setEnableLoader(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id, speciality_id]);

  const handleShowAll = (isChecked) => {
    setFilter({
      ...filter,
      show_all: isChecked,
    });
  };

  const handlePromptView = (id) => {
    setOpenPromptViewId(id);
    setOpenPromptViewModal(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  }

  useDocumentTitle("Speciality Prompt");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24 docpro_mb_0">
          Prompt Setting
        </h2>
        <div className="docpro_page_heading_btns">
          {(can_visit("administrator") && can_visit("administrator_prompt_create")) && (
            <button
              className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
              onClick={() => {
                setNewPromptModalOpen(true);
                setPreviousScrollPosition(window.scrollY);
                window.scrollTo(0, 0);
              }}
            >
              <Plus width={20} height={20} />
              <span>Add Prompt</span>
            </button>
          )}
        </div>
      </div>
      <Spacing lg="15" sm="15" />

      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <form className="docpro_search_form width_100">
            <input
              type="text"
              placeholder="Search"
              className="docpro_search_input"
              onChange={handleChangeSearchTerm}
            />
            <button className="docpro_search_btn">
              <Search width={20} height={20} />
            </button>
          </form>

          <div className="docpro_checkbox">
            <input type="checkbox" name="check_all_yes" 
              onChange={(e) => handleShowAll(e.target.checked)}
            />
            <label style={{ marginLeft: '8px' }}>Show All Account</label>
          </div>
        </div>

        <PromptDataTable
          tableData={promptData}
          reloadData={fetchPromptData}
          reloadDataFilter={filter}
          dataLoading={enableLoader}
          setDataLoading={setEnableLoader}
          setEditModalOpen={setEditPrompModalOpen}
          setEditData={setPrompEditData}
          handleDeleteRecord={handleDeleteRecord}
          handlePromptView={handlePromptView}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />

        {(promptData?.length > 0 && !enableLoader) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>


      {newPromptModalOpen &&
        <AddAndEditPromptModal
          account_id = {account_id}
          location_id = {location_id}
          speciality_id = {speciality_id}
          reloadData = {fetchPromptData}
          reloadDataFilter = {filter}
          editModal = {false}
          activeModal = {newPromptModalOpen}
          setActiveModal = {setNewPromptModalOpen}
          previousScrollPosition={previousScrollPosition}
        />
      }

      {editPrompModalOpen &&
        <AddAndEditPromptModal
          account_id = {account_id}
          location_id = {location_id}
          speciality_id = {speciality_id}
          reloadData = {fetchPromptData}
          reloadDataFilter = {filter}
          editModal = {true}
          editValue = {prompEditData}
          activeModal = {editPrompModalOpen}
          setActiveModal = {setEditPrompModalOpen}
          previousScrollPosition={previousScrollPosition}
        />
      }

      {openPromptViewModal && (
        <PromptSingleViewModal
          account_id = {account_id}
          location_id = {location_id}
          speciality_id = {speciality_id}
          openPromptViewId = {openPromptViewId}
          activeModal={openPromptViewModal}
          setActiveModal={setOpenPromptViewModal}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
